import React from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import { ProfilePictureUpload } from '@/pages/lists/components/ProfilePictureUpload';

export interface ListFormData {
  name: string;
  description: string;
  coverArt: string;
}

export interface ListFormFieldsProps {
  /** Current form values */
  values: ListFormData;
  /** Callback when name changes */
  onNameChange: (value: string) => void;
  /** Callback when description changes */
  onDescriptionChange: (value: string) => void;
  /** Callback when cover art changes */
  onCoverArtChange: (value: string) => void;
  /** Name validation error message */
  nameError?: string;
  /** Callback when name field loses focus */
  onNameBlur?: () => void;
  /** Placeholder text for name input */
  namePlaceholder?: string;
  /** Placeholder text for description input */
  descriptionPlaceholder?: string;
  /** Maximum length for description */
  descriptionMaxLength?: number;
  /** Color scheme for focus states */
  colorScheme?: 'purple' | 'blue' | 'green' | 'orange';
  /** Whether to show the description field (default: true) */
  showDescription?: boolean;
}

/**
 * ListFormFields - Reusable form fields for creating/editing lists
 *
 * Contains name, description, and cover image fields that can be
 * shared between different list creation modals.
 */
export const ListFormFields: React.FC<ListFormFieldsProps> = ({
  values,
  onNameChange,
  onDescriptionChange,
  onCoverArtChange,
  nameError,
  onNameBlur,
  namePlaceholder = 'Enter list name',
  descriptionPlaceholder = "What's this list for?",
  descriptionMaxLength = 200,
  colorScheme = 'purple',
  showDescription = true,
}) => {
  const focusColor = `${colorScheme}.400`;

  return (
    <VStack spacing={5} align="stretch">
      {/* List name input */}
      <FormControl isRequired isInvalid={!!nameError}>
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
          List Name
        </FormLabel>
        <Input
          placeholder={namePlaceholder}
          value={values.name}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={onNameBlur}
          borderRadius="lg"
          bg="gray.50"
          _focus={{ bg: 'white', borderColor: focusColor }}
          data-testid="collab-name-input"
        />
        <FormErrorMessage>{nameError}</FormErrorMessage>
      </FormControl>

      {/* Description input */}
      {showDescription && (
        <FormControl>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            Description
            <Text as="span" color="gray.400" fontWeight="normal" ml={1}>
              (optional)
            </Text>
          </FormLabel>
          <Textarea
            placeholder={descriptionPlaceholder}
            value={values.description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            borderRadius="lg"
            bg="gray.50"
            _focus={{ bg: 'white', borderColor: focusColor }}
            rows={3}
            maxLength={descriptionMaxLength}
          />
          <Text fontSize="xs" color="gray.400" textAlign="right" mt={1}>
            {values.description.length}/{descriptionMaxLength}
          </Text>
        </FormControl>
      )}

      {/* Cover Art */}
      <FormControl>
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
          Cover Image
          <Text as="span" color="gray.400" fontWeight="normal" ml={1}>
            (optional)
          </Text>
        </FormLabel>
        <Box display="flex" justifyContent="center">
          <ProfilePictureUpload
            currentImageUrl={values.coverArt}
            onImageUpload={onCoverArtChange}
          />
        </Box>
      </FormControl>
    </VStack>
  );
};

/**
 * Hook to manage list form state and validation
 */
export const useListFormState = (initialValues?: Partial<ListFormData>) => {
  const [name, setName] = React.useState(initialValues?.name ?? '');
  const [description, setDescription] = React.useState(initialValues?.description ?? '');
  const [coverArt, setCoverArt] = React.useState(initialValues?.coverArt ?? '');
  const [nameError, setNameError] = React.useState('');

  const validateName = (value: string): boolean => {
    if (!value.trim()) {
      setNameError('List name is required');
      return false;
    }
    if (value.trim().length < 3) {
      setNameError('Name must be at least 3 characters');
      return false;
    }
    if (value.trim().length > 50) {
      setNameError('Name must be less than 50 characters');
      return false;
    }
    setNameError('');
    return true;
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (nameError) {
      validateName(value);
    }
  };

  const reset = () => {
    setName(initialValues?.name ?? '');
    setDescription(initialValues?.description ?? '');
    setCoverArt(initialValues?.coverArt ?? '');
    setNameError('');
  };

  const values: ListFormData = {
    name,
    description,
    coverArt,
  };

  const isValid = name.trim().length >= 3 && name.trim().length <= 50;

  return {
    values,
    nameError,
    handleNameChange,
    setDescription,
    setCoverArt,
    validateName,
    reset,
    isValid,
  };
};

export default ListFormFields;
